import React, { useCallback, useEffect } from 'react';
import { useAuth } from '../../../context/Auth';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import { dashboardSummaryService, MonitoredPlatesDashboardDetection, MonitoredPlatesDashboardInfo } from '../../../services/central-api/dashboard-summary';
import { DashBoardParamList } from '../../../typings/Params';
import { useQuery } from '@tanstack/react-query';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import { ActivityIndicator, ScrollView, TouchableOpacity, View } from 'react-native';
import { WindowInformation } from '../../../services/window-information';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import Divider from '../../../components/Divider';
import DoughnutChart from '../../deac/Dashboard/DoughnutChart';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import { faCameraCctv, faLocationDot, faMotorcycle } from '@fortawesome/pro-regular-svg-icons';
import { MyImage } from '../../../components/MyImage';
import { RootNavigation } from '../../../Routes';

interface Props {
    filters: DashBoardParamList['DashboardPlateMonitoring'];
    setFilters: React.Dispatch<React.SetStateAction<DashBoardParamList['DashboardPlateMonitoring']>>;
    navigation: RootNavigation;
    setLastDashboardCalculation: React.Dispatch<React.SetStateAction<number | undefined>>;
    dashboardData: MonitoredPlatesDashboardInfo | undefined;
    setDashboardData: React.Dispatch<React.SetStateAction<MonitoredPlatesDashboardInfo | undefined>>;
}

export default function Dashboard({ filters, setFilters, navigation, setLastDashboardCalculation, dashboardData, setDashboardData }: Props) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const { user: authenticatedUser } = useAuth();

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['plateGroupModal', filters],
        queryFn: () => dashboardSummaryService.getDashboardInfo(filters),
        enabled: !!authenticatedUser && filters.groupId != 0,
    });

    useEffect(() => {
        if (!data) {
            return;
        }
        setDashboardData(data);
        setLastDashboardCalculation(data.lastCalculationTime ?? undefined);
    }, [data]);

    const fiveMinutesInMilliseconds = 300000;
    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            refetch();

            const interval = setInterval(() => {
                if (filters.fixedPeriod != 'select') {
                    let beginIn = undefined;
                    const endIn = moment().valueOf();

                    if (filters.fixedPeriod === 'lastHour') {
                        beginIn = moment().subtract(1, 'hour').valueOf();
                    } else if (filters.fixedPeriod === 'last12Hour') {
                        beginIn = moment().subtract(12, 'hours').valueOf();
                    } else if (filters.fixedPeriod === 'last24Hours') {
                        beginIn = moment().subtract(24, 'hours').valueOf();
                    } else if (filters.fixedPeriod === 'weekly') {
                        beginIn = moment().subtract(1, 'week').valueOf();
                    } else if (filters.fixedPeriod === 'fortnightly') {
                        beginIn = moment().subtract(2, 'weeks').valueOf();
                    } else if (filters.fixedPeriod === 'monthly') {
                        beginIn = moment().subtract(30, 'days').valueOf();
                    }

                    navigation.setParams({
                        ...filters,
                        beginIn: beginIn,
                        endIn: endIn,
                    });

                    setFilters({
                        ...filters,
                        beginIn: beginIn,
                        endIn: endIn,
                    });
                } else {
                    refetch();
                }
            }, fiveMinutesInMilliseconds);
            return () => clearInterval(interval);
        }
    }, [isFocused, filters]));


    function getPeriodMessage() {
        if (filters.fixedPeriod === 'select' && filters.beginIn && filters.endIn) {
            return `${moment(filters.beginIn).format('DD/MM')} - ${moment(filters.endIn).format('DD/MM')}`;
        } else if (filters.fixedPeriod === 'lastHour') {
            return translate('lastHour');
        } else if (filters.fixedPeriod === 'last12Hour') {
            return translate('last12Hour');
        } else if (filters.fixedPeriod === 'last24Hours') {
            return translate('last24Hours');
        } else if (filters.fixedPeriod === 'weekly') {
            return translate('last7days');
        } else if (filters.fixedPeriod === 'fortnightly') {
            return translate('last15days');
        } else if (filters.fixedPeriod === 'monthly') {
            return translate('last30days');
        }
    }

    function getAllCapturedPlates() {
        if (dashboardData === undefined) {
            return 0;
        }

        return dashboardData.morningPlatesQty
            + dashboardData.afternoonPlatesQty
            + dashboardData.nightPlatesQty
            + dashboardData.earlyMorningPlatesQty;
    }

    function getNameText(detection: MonitoredPlatesDashboardDetection) {
        if (detection.camera) {
            return `${detection.camera.title} - [${translate(detection.camera.type)}]`;
        } else if (detection.unitName) {
            return `${detection.unitName.name} - [${translate('mobileUnit')}]`;
        } else {
            return '';
        }
    }

    return (
        !dashboardData && isLoading
            ? <View>
                <ActivityIndicator size="large" style={{ minHeight: 190 }} color={theme.colors.iconColor} />
            </View>
            : <ScrollView contentContainerStyle={styles.wrapper}>
                <View style={styles.header(windowInfo.isMobile)}>
                    <View style={styles.headerCard}>
                        <View style={styles.headerTitleContainer}>
                            <MyAppText style={styles.headerTitleMainText}>{translate('uniqueCapturedPlates')}</MyAppText>
                            <MyAppText style={styles.headerTitlePeriodText}>{getPeriodMessage()}</MyAppText>
                        </View>
                        <View style={styles.headerPlateQuantityContainer}>
                            {isLoading
                                ? <ActivityIndicator size='small' color={'#4073D0'} />
                                : <MyAppText style={styles.headerTitleQuantityText('#4073D0')}>{dashboardData?.uniquePlatesQty || 0}</MyAppText>
                            }
                            <MyAppText style={styles.headerTitlePlateText('#4073D0')}>{translate('plates')}</MyAppText>
                        </View>
                    </View>
                    <Divider
                        color={theme.colors.neutral['100']}
                        orientation={windowInfo.isMobile ? 'horizontal' : 'vertical'}
                        style={windowInfo.isMobile ? { width: '50%' } : { height: '50%' }}
                    />
                    <View style={styles.headerCard}>
                        <View style={styles.headerTitleContainer}>
                            <MyAppText style={styles.headerTitleMainText}>{translate('capturedPlatesInPeriod')}</MyAppText>
                            <MyAppText style={styles.headerTitlePeriodText}>{getPeriodMessage()}</MyAppText>
                        </View>
                        <View style={styles.headerPlateQuantityContainer}>
                            {isLoading
                                ? <ActivityIndicator size='small' color={'#D07740'} />
                                : <MyAppText style={styles.headerTitleQuantityText('#D07740')}>{getAllCapturedPlates()}</MyAppText>
                            }
                            <MyAppText style={styles.headerTitlePlateText('#D07740')}>{translate('plates')}</MyAppText>
                        </View>
                    </View>
                </View>
                <View style={styles.graphsContainer(!windowInfo.isLaptopOrDesktop)}>
                    <View style={[styles.chartContainer, !windowInfo.isMobile && { flex: 1 }]}>
                        <DoughnutChart
                            chartData={dashboardData?.rankingPlates.map(p => p[1]) || []}
                            labels={dashboardData?.rankingPlates.map(p => p[0]) || []}
                            sectionColors={['#9EAD4C', '#874DB9', '#3A6DCD', '#D27841', '#4B995A']}
                            linesColors={[
                                UnistylesRuntime.themeName === 'dark' ? '#EAFD82' : '#535C1E',
                                UnistylesRuntime.themeName === 'dark' ? '#C481FF' : '#522579',
                                UnistylesRuntime.themeName === 'dark' ? '#72A3FF' : '#203F78',
                                UnistylesRuntime.themeName === 'dark' ? '#FFA56D' : '#7C3B13',
                                UnistylesRuntime.themeName === 'dark' ? '#66D87B' : '#1F5C2A'
                            ]}
                            legendAlign='right'
                            isLoading={isLoading}
                        >
                            <DoughnutChart.Header>
                                <View style={styles.chartHeaderContainer}>
                                    <MyAppText style={styles.chartHeaderTitle}>{translate('topFivePlates')}</MyAppText>
                                    <MyAppText style={styles.chartHeaderPeriodText}>{getPeriodMessage()}</MyAppText>
                                </View>
                            </DoughnutChart.Header>
                        </DoughnutChart>
                    </View>
                    <View style={[styles.chartContainer, !windowInfo.isMobile && { flex: 1 }]}>
                        <DoughnutChart
                            chartData={
                                dashboardData?.morningPlatesQty || dashboardData?.afternoonPlatesQty || dashboardData?.nightPlatesQty || dashboardData?.earlyMorningPlatesQty
                                    ? [dashboardData?.morningPlatesQty || 0, dashboardData?.afternoonPlatesQty || 0, dashboardData?.nightPlatesQty || 0, dashboardData?.earlyMorningPlatesQty || 0]
                                    : []}
                            labels={[translate('morning'), translate('afternoon'), translate('nighttime'), translate('earlyMorning')]}
                            sectionColors={['#CCA94B', '#6DB4D3', '#686868', '#5172B8']}
                            linesColors={['#796121', '#275A70', UnistylesRuntime.themeName === 'dark' ? '#9A9A9A' : '#242424', UnistylesRuntime.themeName === 'dark' ? '#6DB4D3' : '#1D366B']}
                            legendAlign='right'
                            isLoading={isLoading}
                        >
                            <DoughnutChart.Header>
                                <View style={styles.chartHeaderContainer}>
                                    <MyAppText style={styles.chartHeaderTitle}>{translate('capturedPlatesPerPeriod')}</MyAppText>
                                    <MyAppText style={styles.chartHeaderPeriodText}>{getPeriodMessage()}</MyAppText>
                                </View>
                            </DoughnutChart.Header>

                        </DoughnutChart>
                    </View>


                </View>
                <View style={[styles.lastCapturesContainer, (dashboardData?.detections.length === 0 || isLoading) && { flex: 1, minHeight: 250 }]}>
                    <View style={[styles.capturesHeaderContainer, (dashboardData?.detections.length === 0 || isLoading) && { alignItems: 'flex-start' }]}>
                        <MyAppText style={styles.headerTitleMainText}>{translate('last10CapturesRegistered')}</MyAppText>
                        <MyAppText style={styles.headerTitlePeriodText}>{getPeriodMessage()}</MyAppText>
                    </View>
                    <ScrollView
                        horizontal style={[{ paddingLeft: 16, paddingRight: 16 }, (!dashboardData?.detections || dashboardData.detections.length === 0 || isLoading) && { height: '100%', width: '100%' }]}
                        contentContainerStyle={[{ gap: 80 }, (!dashboardData?.detections || dashboardData.detections.length === 0 || isLoading) && { height: '100%', width: '100%', justifyContent: 'center' }]}
                    >
                        {isLoading
                            ? <View style={styles.allSpaceCentered}>
                                <ActivityIndicator size='large' color={theme.colors.primary['500']} />
                            </View>
                            : <>
                                {!dashboardData?.detections || dashboardData.detections.length === 0
                                    ? <View style={styles.allSpaceCentered}>
                                        <MyAppText style={styles.noDataText}>{translate('noRecordsForSelectedPeriod')}</MyAppText>
                                    </View>
                                    : <>
                                        {dashboardData?.detections.map(detection => (
                                            <View key={detection.id} style={styles.captureCard}>
                                                <View style={styles.captureCardImage}>
                                                    <MyImage
                                                        imageUrl={detection.imageUrl}
                                                        style={{ width: 334, height: 120 }}
                                                    />
                                                </View>
                                                <View style={styles.captureCardContentContainer}>
                                                    <View style={styles.captureCardContentHeader}>
                                                        <MyAppText style={styles.captureCardContentMainText}>{translate('vehicleData')}</MyAppText>
                                                        <MyAppText style={styles.captureCardContentHeaderPlateTitle}>{translate('plate')}: <MyAppText style={styles.captureCardContentHeaderPlateText}>{detection.plate}</MyAppText></MyAppText>
                                                    </View>
                                                    <View style={styles.captureCardContentBody}>
                                                        <MyAppText style={styles.captureCardContentMainText}>{translate('visualizedOn')}</MyAppText>
                                                        <View style={{ flexDirection: 'column', gap: 2 }}>
                                                            <View style={{ flexDirection: 'row', gap: 8 }}>
                                                                <FontAwesomeIcon icon={(detection.camera ? faCameraCctv : faMotorcycle) as IconDefinition} fontSize={20} color={theme.colors.iconColor} />
                                                                <MyAppText style={styles.captureCardContentBodyText}>{getNameText(detection)}</MyAppText>
                                                                {authenticatedUser?.permissions.lpr_module_access && <View style={{ flex: 1, flexWrap: 'wrap', alignContent: 'flex-end' }}>
                                                                    <TouchableOpacity onPress={() => navigation.navigate('LPR', { screen: 'LiveEventLprDetection', params: { detectionId: detection.id } })}>
                                                                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} fontSize={16} color={theme.colors.primary['500']} />
                                                                    </TouchableOpacity>
                                                                </View>}
                                                            </View>
                                                            <View style={{ flexDirection: 'row', gap: 8 }}>
                                                                <FontAwesomeIcon icon={faCalendar} fontSize={20} color={theme.colors.iconColor} />
                                                                <MyAppText style={styles.captureCardContentBodyText}>{moment(detection.time).format('DD/MM/YYYY - HH:mm:ss')}</MyAppText>
                                                            </View>
                                                            <View style={{ flexDirection: 'row', gap: 8 }}>
                                                                <FontAwesomeIcon icon={faLocationDot as IconDefinition} fontSize={20} color={theme.colors.iconColor} />
                                                                <MyAppText style={styles.captureCardContentBodyText}>{detection.address}</MyAppText>
                                                            </View>
                                                        </View>
                                                    </View>
                                                </View>
                                            </View>
                                        ))}
                                    </>
                                }
                            </>
                        }
                    </ScrollView>
                </View>
            </ScrollView >
    );
}

const styleSheet = createStyleSheet((theme) => ({
    wrapper: {
        flex: 1,
        backgroundColor: theme.colors.dataTable.background,
        padding: 10,
        gap: 16,
        borderRadius: 4,
    },
    header: (isMobile: boolean) => ({
        flexDirection: isMobile ? 'column' : 'row',
        borderWidth: 1,
        borderRadius: 8,
        borderColor: theme.colors.neutral['100']
    }),
    headerCard: {
        paddingHorizontal: 20,
        paddingVertical: 12,
        gap: 16,
        flex: 1,
    },
    headerTitleContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    headerPlateQuantityContainer: {
        flexDirection: 'row',
        gap: 8,
        alignItems: 'center'
    },
    headerTitleMainText: {
        fontWeight: '600',
        fontSize: 16,
        color: theme.colors.secondary['400']
    },
    headerTitlePeriodText: {
        fontWeight: '600',
        fontSize: 12,
        color: theme.colors.neutral['400']
    },
    headerTitleQuantityText: (color: string) => ({
        fontWeight: '600',
        fontSize: 20,
        color: color
    }),
    headerTitlePlateText: (color: string) => ({
        fontWeight: '600',
        fontSize: 16,
        color: color
    }),
    graphsContainer: (isColumn: boolean) => ({
        flexDirection: isColumn ? 'column' : 'row',
        gap: 16,
    }),
    chartContainer: {
        minHeight: 260,
    },
    chartHeaderContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
    },
    chartHeaderActions: {
        flexDirection: 'row',
        columnGap: 8,
        alignItems: 'center'
    },
    chartHeaderTitle: {
        fontSize: 16,
        color: theme.colors.secondary['400'],
        fontWeight: '600',
        flex: 0.75
    },
    chartHeaderPeriodText: {
        fontSize: 12,
        color: theme.colors.neutral['400'],
        fontWeight: '600',
    },
    lastCapturesContainer: {
        flexDirection: 'column',
        gap: 16,
        borderWidth: 1,
        borderRadius: 8,
        borderColor: theme.colors.neutral['100'],
        paddingVertical: 16,
    },
    capturesHeaderContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        flex: 1,
        marginHorizontal: 16
    },
    captureCard: {
        flexDirection: 'column',
        gap: 16,
        maxWidth: 334,
    },
    captureCardImage: {
        borderWidth: 1,
        borderRadius: 8,
        borderColor: '#BBBBBB',
        width: 334,
        height: 120
    },
    captureCardContentContainer: {
        flexDirection: 'column',
        gap: 8,
    },
    captureCardContentHeader: {
        flexDirection: 'column',
        gap: 4,
    },
    captureCardContentMainText: {
        fontWeight: '700',
        fontSize: 14,
        color: theme.colors.secondary['400']
    },
    captureCardContentHeaderPlateTitle: {
        fontWeight: '700',
        fontSize: 12,
        color: theme.colors.secondary['400']
    },
    captureCardContentHeaderPlateText: {
        fontWeight: '600',
        fontSize: 12,
        color: theme.colors.neutral['400']
    },
    captureCardContentBody: {
        flexDirection: 'column',
        gap: 4,
    },
    captureCardContentBodyText: {
        fontWeight: '400',
        fontSize: 12,
        color: '#6B6B6B'
    },
    allSpaceCentered: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    noDataText: {
        fontSize: 14,
        fontWeight: '500',
        color: theme.colors.neutral['400'],
        textAlign: 'center'
    },
}));
